// ./src/config/featuresCode.ts
//
// The app's view of plan entitlements: the CODES come from the generated file (never
// hand-edited); this file adds only the app-side concerns.
//
// ⭐ ONE SOURCE OF TRUTH: eventsmagic-api `src/boundary/appFeatures.ts`.
// It is emitted here by `npm run gen:feature-codes` in that repo, and the plan catalogs
// that write the ms_auth rows are typed against the same declaration — so a code cannot
// exist in one place and not another. There is nothing left to keep in sync by hand.
//
// The VALUES for these codes live on the plan in ms_auth (`app_plan_features`,
// space-scoped so each brand has its own catalog). Look them up via:
//   useApp().hasAppPlanFeature(code)   → boolean gate
//   useApp().getAppPlanFeature(code)   → value (e.g. a numeric limit)
//   useNumericFeature(code)            → { maxValue } for numeric limits
//
// Design and reasoning behind every code: eventsmagic-api `docs/MONETIZATION.md`.

import { GENERATED_FEATURE_CODES, GENERATED_PLAN_CODES } from "./featureCodes.generated";

export type { FeatureCode, PlanCode } from "./featureCodes.generated";

/** The feature vocabulary. Generated — add new codes in eventsmagic-api, then regenerate. */
const FEATURE_CODES = GENERATED_FEATURE_CODES;

/** Plan codes provisioned for eventsmagic. partylabz's plans are step 3. */
const PLAN_CODES = GENERATED_PLAN_CODES;

/**
 * ⚠ PRE-LAUNCH KILL SWITCH — paywalls are still OFF, but the reason has CHANGED.
 *
 * It used to be "there are no plans, so every lookup returns nothing and every gate reads
 * as locked". That is fixed: `free` / `pro` / `business` and all 27 feature rows are
 * provisioned for both the eventsmagic and partylabz spaces, and existing accounts carry
 * `current_plan = 'free'`, so lookups now resolve.
 *
 * What is still missing before this can flip to `true`:
 *   1. The EVENT-SCOPED entitlement RESOLUTION. ✅ The seam is built (2026-09-10):
 *      `useFeatureGate().hasFeature(code, { eventId })` is now the only function that answers
 *      entitlement, and it already takes the event. ⚠ It cannot yet ANSWER the event half —
 *      nothing grants an event upgrade (ROADMAP A9.4) and nothing reads one back, on either
 *      side. Wiring it is a change to that one function (MONETIZATION.md §2).
 *   2. ⚠ Flipping it now would put the OWNER on `free` — which means `max_users_qty: 1`
 *      (no team invitations, the original lockout), `guest_list: false` (headcount only)
 *      and no production tooling. Assign the owner a plan first, or this switch bites the
 *      same hand twice.
 *   3. The server side. ⚠ This constant is FRONT END ONLY — an ms_auth refusal still
 *      stands and has to be lifted there too.
 *
 * ⚠ Do NOT read this constant to decide whether to enforce a gate — call `arePaywallsActive()`
 * below, so the preview switch reaches every gate rather than leaving some permanently open.
 *
 * `grep PAYWALLS_ENABLED` finds every place it reaches. Tracked as ROADMAP A2.5.
 *
 * ═══ ON since 2026-09-11 (owner's call) ═══════════════════════════════════════════════
 *
 * ⚠⚠ IT WAS TURNED ON TO MATCH THE SERVER, WHICH IS ALREADY ENFORCING. eventsmagic-api's `.env`
 * carries `PAYWALLS_ENABLED=true` — verified by resolving `config.paywallsEnabled`, not by
 * reading the docs, which still say both switches are false. (`.env` is gitignored, so when it
 * was set cannot be recovered; it was last written 2026-09-11.)
 *
 * ⚠ While the two disagreed, they were in the WORST of the four combinations: the server
 * refused a create that the UI had shown as perfectly available, so a host filled in a budget
 * item and was refused at Save — with nothing on the page to warn them.
 *
 * ⚠ That is the direction the codebase is written to avoid ("a client may be LOOSER than its
 * server, never STRICTER" — the loose side is meant to be a NOTICE, not silence). Turning this
 * on makes the two agree: the page is locked up front, for the same reason the server would
 * have refused.
 *
 * ⚠ Point 2 above is now DELIBERATE rather than a hazard. The owner IS on `free` and wants to
 * see exactly what a free host sees; switching their own plan is how they look at the rest.
 *
 * ⚠ `?paywalls=preview` still works but is now REDUNDANT — `arePaywallsActive()` is already
 * true. `?paywalls=off` clears the remembered preview flag but CANNOT turn the gates off while
 * this constant is true, which is intended: the gates are the product's behaviour now, not a
 * preview of it.
 */
const PAYWALLS_ENABLED = true;

/**
 * ⚠ PREVIEW SWITCH — lets the owner SEE the locked UI without flipping the kill switch.
 *
 * The gates above are off by design, which leaves the whole of A2.5.1 invisible: with
 * paywalls disabled every gate renders unlocked, so there is no way to check the copy, the
 * badges or the upgrade routes short of putting a real account on `free` and locking
 * yourself out of your own product.
 *
 * Turn it on by loading any app URL with `?paywalls=preview`, off again with `?paywalls=off`.
 * The choice is remembered in localStorage for that browser only.
 *
 * ⚠ It is CLIENT-ONLY and changes nothing server-side — the API still allows everything
 * while its own `PAYWALLS_ENABLED` is false, so a preview session can walk into a control
 * the UI locked and find the save works. That is the point: it previews the UI, it does not
 * simulate the server.
 */
const PAYWALL_PREVIEW_KEY = "em.paywallPreview";

/**
 * ⚠ Resolved ONCE at module load, not per call. `arePaywallsActive()` is read during the
 * render of every gated page and inside `useFeatureGate`'s memo deps, and a `localStorage`
 * hit on each of those is both wasteful and a source of tearing (two components could read
 * different values within one render pass if the key changed mid-render). Toggling the
 * preview therefore takes effect on the next page load, which is also the clearer behaviour.
 */
const readPaywallPreview = (): boolean => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("paywalls");

    if (fromUrl === "preview") {
      window.localStorage.setItem(PAYWALL_PREVIEW_KEY, "1");

      return true;
    }

    if (fromUrl === "off") {
      window.localStorage.removeItem(PAYWALL_PREVIEW_KEY);

      return false;
    }

    return window.localStorage.getItem(PAYWALL_PREVIEW_KEY) === "1";
  } catch {
    // A private window, blocked site data, or no `window` at all. Never let a preview
    // convenience throw on the way into the app.
    return false;
  }
};

/**
 * Whether the app should ENFORCE plan gates right now.
 *
 * Every gate must ask this rather than reading PAYWALLS_ENABLED directly, so the preview
 * switch reaches all of them and none is left permanently unlocked.
 */
const paywallPreview = readPaywallPreview();

const arePaywallsActive = (): boolean => {
  return PAYWALLS_ENABLED || paywallPreview;
};

/** True when the gates are only active because of the preview switch, so the UI can say so. */
const isPaywallPreview = (): boolean => {
  return !PAYWALLS_ENABLED && paywallPreview;
};



// Capabilities that are PREMIUM (not on the free tier). Advisory for the client — it lets
// the UI badge a control and route to upgrade — while the plan remains the source of truth
// via hasAppPlanFeature(). Numeric LIMITS aren't listed here; they're enforced by comparing
// against the plan's value.
//
// ⚠ TICKETING and ENGAGEMENT are deliberately ABSENT: both are free on every eventsmagic
// plan. Gating checkout would block the very transaction the service fee exists to capture,
// and engagement costs us nothing per use while making a free event page worth sharing.
// (On partylabz, ENGAGEMENT is the main upgrade reason — hence "advisory": when partylabz
// plans land, this set becomes brand-aware rather than a single constant.)
const PREMIUM_FEATURE_CODES: ReadonlySet<string> = new Set<string>([
  FEATURE_CODES.GUEST_LIST,
  FEATURE_CODES.RICH_MEDIA,
  FEATURE_CODES.PRO_REGISTRATION,
  FEATURE_CODES.PRODUCTION,
  FEATURE_CODES.PROMOTIONS,
  FEATURE_CODES.COMMERCIAL,
  FEATURE_CODES.BRANDING,
  FEATURE_CODES.AUTOMATION,
  FEATURE_CODES.RICH_CHANNELS,
  FEATURE_CODES.INSIGHTS,
  FEATURE_CODES.BYO_SENDER,
]);

/** Whether a feature code is a premium (non-free) capability. */
export const isPremiumFeature = (code: string): boolean => PREMIUM_FEATURE_CODES.has(code);

export {
  // Constants
  FEATURE_CODES,
  PLAN_CODES,
  PREMIUM_FEATURE_CODES,
  PAYWALLS_ENABLED,
  // Runtime switches
  arePaywallsActive,
  isPaywallPreview,
};
